import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Typography,
  Button,
  Space,
  Spin,
  Table,
  Switch,
  Modal,
} from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  MinusOutlined,
  LoadingOutlined,
  QuestionCircleOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { Tooltip } from "antd";
import moment from "moment";
import axios from "axios";
import ENVIRONMENT from "../../../../environments";
import useTableFilter from "../../UseTableFilter/useTableFilter";
import { exportToCSV } from "../../Utilities";

const { Title, Text } = Typography;

const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

const QUICK_FILTERS = [
  { label: "Last 7 Days", days: 7 },
  { label: "Last 30 Days", days: 30 },
];

const NewSignupsChart = ({ chartStyle }) => {
  const [activeFilter, setActiveFilter] = useState(7);
  const [excludeExistingCompanies, setExcludeExistingCompanies] = useState(false);
  const [currentTotal, setCurrentTotal] = useState(null);
  const [previousTotal, setPreviousTotal] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [expiredData, setExpiredData] = useState([]);
  const [isExpiredLoading, setIsExpiredLoading] = useState(false);

  const [isSignupsModalOpen, setIsSignupsModalOpen] = useState(false);
  const [signupDetails, setSignupDetails] = useState([]);
  const [isSignupDetailsLoading, setIsSignupDetailsLoading] = useState(false);

  const [getColumnSearchProps] = useTableFilter();

  const fetchCounts = async (days, excludeExisting) => {
    try {
      setIsLoading(true);

      const currentEnd = moment();
      const currentStart = moment().subtract(days - 1, "days");
      const previousEnd = moment().subtract(days, "days");
      const previousStart = moment().subtract(days * 2 - 1, "days");

      const [currentRes, previousRes] = await Promise.all([
        axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, {
          action: "get_new_signups_count",
          start_date: currentStart.format("YYYY-MM-DD"),
          end_date: currentEnd.format("YYYY-MM-DD"),
          exclude_existing_companies: excludeExisting,
        }),
        axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, {
          action: "get_new_signups_count",
          start_date: previousStart.format("YYYY-MM-DD"),
          end_date: previousEnd.format("YYYY-MM-DD"),
          exclude_existing_companies: excludeExisting,
        }),
      ]);

      const sumCounts = (data) =>
        (data || []).reduce((acc, item) => acc + parseInt(item.count, 10), 0);

      setCurrentTotal(sumCounts(currentRes.data));
      setPreviousTotal(sumCounts(previousRes.data));
    } catch (error) {
      console.error("Error fetching signup counts:", error);
      setCurrentTotal(null);
      setPreviousTotal(null);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchExpiredSubscribers = async () => {
    try {
      setIsExpiredLoading(true);
      const response = await axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, {
        action: "get_expired_paid_subscribers",
      });
      setExpiredData(
        (response.data || []).map((item, i) => ({ ...item, key: i }))
      );
    } catch (error) {
      console.error("Error fetching expired subscribers:", error);
      setExpiredData([]);
    } finally {
      setIsExpiredLoading(false);
    }
  };

  useEffect(() => {
    fetchCounts(activeFilter, excludeExistingCompanies);
    fetchExpiredSubscribers();
  }, []);

  const handleQuickFilter = (days) => {
    setActiveFilter(days);
    fetchCounts(days, excludeExistingCompanies);
  };

  const handleExcludeExistingToggle = (checked) => {
    setExcludeExistingCompanies(checked);
    fetchCounts(activeFilter, checked);
  };

  const fetchSignupDetails = async (days, excludeExisting) => {
    try {
      setIsSignupDetailsLoading(true);
      const end = moment();
      const start = moment().subtract(days - 1, "days");
      const response = await axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, {
        action: "get_new_signup_user_details",
        start_date: start.format("YYYY-MM-DD"),
        end_date: end.format("YYYY-MM-DD"),
        exclude_existing_companies: excludeExisting,
      });
      setSignupDetails(
        (response.data || []).map((item, i) => ({ ...item, key: i }))
      );
    } catch (error) {
      console.error("Error fetching signup details:", error);
      setSignupDetails([]);
    } finally {
      setIsSignupDetailsLoading(false);
    }
  };

  const handleViewDetails = () => {
    setIsSignupsModalOpen(true);
    fetchSignupDetails(activeFilter, excludeExistingCompanies);
  };

  const handleSignupsExport = () => {
    const cols = [
      { title: "Username",     dataIndex: "username" },
      { title: "Email",        dataIndex: "email" },
      { title: "Profile Name", dataIndex: "profile_name" },
      { title: "Company",      dataIndex: "company_display_name" },
      { title: "Signed Up On", dataIndex: "signed_up_on" },
    ];
    const suffix = excludeExistingCompanies ? "-new-companies" : "";
    exportToCSV(`new-signups-last-${activeFilter}-days${suffix}`, cols, signupDetails);
  };

  const handleChurnedExport = () => {
    const cols = [
      { title: "Company",    dataIndex: "display_name" },
      { title: "Plan",       dataIndex: "subscription_name" },
      { title: "Users",      dataIndex: "user_count" },
      { title: "Expired On", dataIndex: "expired_on" },
    ];
    exportToCSV("churned-subscribers", cols, expiredData);
  };

  const getTrend = () => {
    if (currentTotal === null || previousTotal === null) return null;
    if (previousTotal === 0 && currentTotal === 0)
      return { icon: <MinusOutlined />, color: "#8c8c8c", text: "No change", pct: null };
    if (previousTotal === 0)
      return { icon: <ArrowUpOutlined />, color: "#52c41a", text: "New signups", pct: null };

    const pct = ((currentTotal - previousTotal) / previousTotal) * 100;
    if (pct > 0)
      return { icon: <ArrowUpOutlined />, color: "#52c41a", text: `+${pct.toFixed(1)}%`, pct };
    if (pct < 0)
      return { icon: <ArrowDownOutlined />, color: "#f5222d", text: `${pct.toFixed(1)}%`, pct };
    return { icon: <MinusOutlined />, color: "#8c8c8c", text: "0%", pct: 0 };
  };

  const trend = getTrend();
  const periodLabel = activeFilter === 7 ? "previous 7 days" : "previous 30 days";

  const signupDetailColumns = [
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      ...getColumnSearchProps("username"),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      ...getColumnSearchProps("email"),
    },
    {
      title: "Profile Name",
      dataIndex: "profile_name",
      key: "profile_name",
      ...getColumnSearchProps("profile_name"),
    },
    {
      title: "Company",
      dataIndex: "company_display_name",
      key: "company_display_name",
      ...getColumnSearchProps("company_display_name"),
    },
    {
      title: "Signed Up On",
      dataIndex: "signed_up_on",
      key: "signed_up_on",
    },
  ];

  const expiredColumns = [
    {
      title: "Company",
      dataIndex: "display_name",
      key: "display_name",
      ...getColumnSearchProps("display_name"),
    },
    {
      title: "Plan",
      dataIndex: "subscription_name",
      key: "subscription_name",
    },
    {
      title: "Users",
      dataIndex: "user_count",
      key: "user_count",
      align: "center",
    },
    {
      title: "Expired On",
      dataIndex: "expired_on",
      key: "expired_on",
    },
  ];

  return (
    <>
      <Col span={12} style={{ display: "flex", flexDirection: "column" }}>
        <Card style={{ ...chartStyle, height: "100%" }}>
          <Title level={5} style={{ marginTop: 0 }}>
            New User Signups
          </Title>
          <Space style={{ marginBottom: 20 }} wrap>
            {QUICK_FILTERS.map(({ label, days }) => (
              <Button
                key={days}
                size="small"
                type={activeFilter === days ? "primary" : "default"}
                onClick={() => handleQuickFilter(days)}
              >
                {label}
              </Button>
            ))}
            <Space size={6}>
              <Switch
                size="small"
                checked={excludeExistingCompanies}
                onChange={handleExcludeExistingToggle}
              />
              <Text style={{ fontSize: 12 }} type="secondary">
                Exclude existing companies' users
              </Text>
            </Space>
          </Space>

          <Spin spinning={isLoading} indicator={antIcon}>
            <div style={{ textAlign: "center", padding: "24px 0" }}>
              <div
                style={{
                  fontSize: 64,
                  fontWeight: 700,
                  lineHeight: 1,
                  color: "#1890ff",
                }}
              >
                {currentTotal !== null ? currentTotal.toLocaleString() : "—"}
              </div>

              {trend && (
                <div style={{ marginTop: 16 }}>
                  <span
                    style={{
                      fontSize: 20,
                      fontWeight: 600,
                      color: trend.color,
                    }}
                  >
                    {trend.icon} {trend.text}
                  </span>
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      vs {previousTotal !== null ? previousTotal.toLocaleString() : "—"} in {periodLabel}
                    </Text>
                  </div>
                </div>
              )}
              <div style={{ marginTop: 20 }}>
                <Button type="default" size="small" onClick={handleViewDetails}>
                  View Details
                </Button>
              </div>
            </div>
          </Spin>
        </Card>
      </Col>

      <Col span={12} style={{ display: "flex", flexDirection: "column" }}>
        <Card style={{ ...chartStyle, height: "100%" }}>
          <Row justify="space-between" align="middle" style={{ marginBottom: 0 }}>
            <Col>
              <Space size={8} align="center">
                <Title level={5} style={{ margin: 0 }}>
                  Churned Subscribers
                </Title>
                <Tooltip title="Companies who cancelled their subscription">
                  <QuestionCircleOutlined style={{ color: "#8c8c8c" }} />
                </Tooltip>
              </Space>
            </Col>
            <Col>
              <Button
                type="primary"
                ghost
                size="small"
                icon={<DownloadOutlined />}
                onClick={handleChurnedExport}
                disabled={!expiredData.length}
                style={{ borderRadius: 5 }}
              >
                Export CSV
              </Button>
            </Col>
          </Row>
          <Table
            columns={expiredColumns}
            dataSource={expiredData}
            loading={{ indicator: antIcon, spinning: isExpiredLoading }}
            pagination={{ pageSize: 5, size: "small" }}
            size="small"
            style={{ marginTop: 16 }}
            scroll={{ x: true }}
          />
        </Card>
      </Col>

      <Modal
        title={`New Signups — Last ${activeFilter} Days${excludeExistingCompanies ? " (New companies only)" : ""}`}
        visible={isSignupsModalOpen}
        onCancel={() => {
          setIsSignupsModalOpen(false);
          setSignupDetails([]);
        }}
        footer={null}
        width="80vw"
        destroyOnClose={true}
        centered
      >
        <Row justify="end" style={{ marginBottom: 12 }}>
          <Button
            type="primary"
            ghost
            icon={<DownloadOutlined />}
            onClick={handleSignupsExport}
            disabled={!signupDetails.length}
            style={{ borderRadius: 5 }}
          >
            Export CSV
          </Button>
        </Row>
        <Table
          columns={signupDetailColumns}
          dataSource={signupDetails}
          loading={{ indicator: antIcon, spinning: isSignupDetailsLoading }}
          pagination={false}
          scroll={{ y: 400 }}
          size="middle"
        />
      </Modal>
    </>
  );
};

export default NewSignupsChart;
